// Size finder engine (v8). Pure logic: no DOM.
//
// How it works
//  1. The store reference guide (cly_TABLE) is unchanged. A customer's height and weight are placed on one continuous
//     size scale: 0 = S, 1 = M, 2 = L, 3 = XL, 4 = 2XL (extrapolated with the guide's own band widths below S / above 2XL).
//  2. Every product size is placed on the same scale (letters, 1-5, EU 36-48 women's, EU 48-56 men's, custom.size_map).
//  3. The fit preference moves the customer on that scale (slim: closer cut = smaller size, oversized: roomier = larger).
//  4. The customer's position is worked out first, with no reference to what this product sells. Only then is it
//     compared with the sizes this product offers:
//       - inside the product's range           -> nearest AVAILABLE size (sold-out sizes are skipped, as before)
//       - slightly past the largest size       -> the largest size is still a reasonable fit, so it is recommended
//       - clearly past the largest size        -> status "tooLarge": the size the customer actually needs, named in the
//                                                 product's own sizing system, and flagged as not available
//       - clearly below the smallest size      -> status "tooSmall": same idea (0 / XS ...), with a wider tolerance
//     "Slightly" is measured on the same scale as the size guide: every size owns a band (its position +/- half the gap
//     to the neighbouring size). The customer may sit up to cly_TOLERANCE of the NEXT size's band beyond the top of the
//     largest size's band and still get the largest size. The fit preference is already part of the position.
//  5. Customer gender is never an input, so a woman on a men's jacket (or the reverse) gets the same treatment.
var cly_TABLE = [
  { size: 'S',   hMin: 155, hMax: 170,      wMin: 38, wMax: 48 },
  { size: 'M',   hMin: 170, hMax: 175,      wMin: 48, wMax: 57 },
  { size: 'L',   hMin: 175, hMax: 180,      wMin: 57, wMax: 66 },
  { size: 'XL',  hMin: 180, hMax: 185,      wMin: 66, wMax: 77 },
  { size: '2XL', hMin: 185, hMax: Infinity, wMin: 77, wMax: 86 }
];
var cly_H_CAP = 15;                     // band width used for the open-ended 2XL height range
var cly_H_WEIGHT = 0.6, cly_W_WEIGHT = 0.4;
var cly_FIT_SHIFT = { slim: -0.35, regular: 0, oversized: 0.35 };   // in size steps
var cly_EDGE_GAP = 0.75;                // below the product's smallest size by more than this -> "smallest size" note
var cly_TOLERANCE = 0.25;               // share of the next size's band allowed beyond the largest size (0.25 = a quarter band)
var cly_TOLERANCE_SMALL = 0.5;          // same idea below the smallest size, more generous: half a band (a full size step from the smallest size's centre)

var cly_LETTER_POS = { XXS: -2, XS: -1, S: 0, M: 1, L: 2, XL: 3, XXL: 4, '2XL': 4, XXXL: 5, '3XL': 5, '4XL': 6 };
// Letter names for the positions beyond the product's range, used when the customer needs a size the product does not sell.
// Position 4 is XXL, or 2XL when the product itself names its sizes that way (see cly_letterFor).
var cly_LETTER_BY_POS = { '-2': 'XXS', '-1': 'XS', 0: 'S', 1: 'M', 2: 'L', 3: 'XL', 5: '3XL', 6: '4XL' };
// Store chart for women's EU numeric sizes (kept from v6).
var cly_EU_WOMEN_POS = { 34: -1, 36: 0, 38: 0.5, 40: 1, 42: 1.5, 44: 2, 46: 3, 48: 4 };
var cly_BASE_POS = { S: 0, M: 1, L: 2, XL: 3, '2XL': 4, XXL: 4 };

// Position of one measurement on the scale, from the guide's bands (continuous, extrapolates at both ends).
function cly_axisPos(v, lows, highs) {
  var last = lows.length - 1;
  for (var i = 0; i <= last; i++) {
    if (v < highs[i] || i === last) return i + (v - lows[i]) / (highs[i] - lows[i]) - 0.5;
  }
}
var cly_H_LOWS = cly_TABLE.map(function (r) { return r.hMin; });
var cly_H_HIGHS = cly_TABLE.map(function (r) { return Math.min(r.hMax, r.hMin + cly_H_CAP); });
var cly_W_LOWS = cly_TABLE.map(function (r) { return r.wMin; });
var cly_W_HIGHS = cly_TABLE.map(function (r) { return r.wMax; });

function cly_position(h, w) {
  return cly_H_WEIGHT * cly_axisPos(h, cly_H_LOWS, cly_H_HIGHS) + cly_W_WEIGHT * cly_axisPos(w, cly_W_LOWS, cly_W_HIGHS);
}

// Turns the product's Size option values into { value, available, pos, kind } items on the scale.
// kind says how the position was found: letter | map | ordinal (1..N) | euWomen | rank (other numeric sizes).
// sizes: [{ value, available }], sizeMap: optional custom.size_map (product size -> base size or list of base sizes).
function cly_mapSizes(sizes, sizeMap) {
  var map = sizeMap;
  if (typeof map === 'string') { try { map = JSON.parse(map); } catch (e) { map = null; } }
  var items = [];
  var numeric = [];
  sizes.forEach(function (s) {
    var raw = String(s.value);
    var key = raw.toUpperCase().replace(/\s+/g, '');
    var pos = null;
    var kind = null;
    if (map && typeof map === 'object' && map[raw] !== undefined) {
      var bases = Array.isArray(map[raw]) ? map[raw] : [map[raw]];
      var ps = bases.map(function (b) { return cly_BASE_POS[String(b).toUpperCase()]; }).filter(function (x) { return x !== undefined; });
      if (ps.length) { pos = ps.reduce(function (a, b) { return a + b; }, 0) / ps.length; kind = 'map'; }
    }
    if (pos === null) {
      var letter = key.match(/^(XXXS|XXS|XS|S|M|L|XL|XXXL|XXL|2XL|3XL|4XL)(?![A-Z0-9])/);   // also "S(US 2-4)"
      if (letter && cly_LETTER_POS[letter[1]] !== undefined) { pos = cly_LETTER_POS[letter[1]]; kind = 'letter'; }
    }
    var item = { value: raw, available: !!s.available, pos: pos, kind: kind };
    if (pos === null) {
      var n = key.match(/^(\d{1,2})(?:-\d+)?$/);                 // "38", "38-1"
      if (n) { item.num = parseInt(n[1], 10); numeric.push(item); }
      else return;                                                // unknown label: ignored
    }
    items.push(item);
  });
  if (numeric.length) {
    var nums = numeric.map(function (i) { return i.num; });
    var max = Math.max.apply(null, nums), min = Math.min.apply(null, nums);
    var sorted = numeric.slice().sort(function (a, b) { return a.num - b.num; });
    if (max <= 9) {                                              // 1..N: ordinal, 1 = S
      numeric.forEach(function (i) { i.pos = i.num - 1; i.kind = 'ordinal'; });
    } else if (min >= 34 && max <= 48) {                         // women's EU chart (36-48)
      numeric.forEach(function (i) { i.pos = cly_EU_WOMEN_POS[i.num] !== undefined ? cly_EU_WOMEN_POS[i.num] : (i.num - 40) / 2 + 1; i.kind = 'euWomen'; });
    } else {                                                     // men's EU 48-56 (or similar): smallest = S, one step per size
      sorted.forEach(function (i, idx) { i.pos = idx; i.kind = 'rank'; });
    }
  }
  return items.filter(function (i) { return i.pos !== null; });
}

function cly_nearest(list, p, fit) {
  return list.slice().sort(function (a, b) {
    var da = Math.abs(a.pos - p), db = Math.abs(b.pos - p);
    if (Math.abs(da - db) > 1e-9) return da - db;
    return fit === 'slim' ? a.pos - b.pos : b.pos - a.pos;       // exact tie: slim -> smaller, otherwise larger
  })[0];
}

// The product's sizing system, so that a size beyond its range is named the way the product names its sizes.
// letters (also custom.size_map) | ordinal | euWomen | rank | mixed (several systems at once: nothing can be inferred).
function cly_system(items) {
  var kinds = {};
  items.forEach(function (i) { kinds[i.kind === 'map' ? 'letter' : i.kind] = true; });
  var list = Object.keys(kinds);
  if (list.length !== 1) return 'mixed';
  return list[0] === 'letter' ? 'letters' : list[0];
}

// Position of the size that follows the product's largest one in its own system (needed to know how wide that band is).
function cly_nextPos(system, maxPos) {
  if (system === 'euWomen') {
    var higher = Object.keys(cly_EU_WOMEN_POS).map(function (k) { return cly_EU_WOMEN_POS[k]; })
      .filter(function (v) { return v > maxPos + 1e-9; });
    if (higher.length) return Math.min.apply(null, higher);
  }
  return maxPos + 1;
}

// Position of the size that precedes the product's smallest one (the mirror of cly_nextPos).
function cly_prevPos(system, minPos) {
  if (system === 'euWomen') {
    var lower = Object.keys(cly_EU_WOMEN_POS).map(function (k) { return cly_EU_WOMEN_POS[k]; })
      .filter(function (v) { return v < minPos - 1e-9; });
    if (lower.length) return Math.max.apply(null, lower);
  }
  return minPos - 1;
}

// Below this position the smallest size is no longer a reasonable recommendation.
function cly_smallestLimit(minPos, prevPos) {
  return minPos - (0.5 + cly_TOLERANCE_SMALL) * (minPos - prevPos);
}

// Beyond this position the largest size is no longer a reasonable recommendation:
// top of its band (half-way to the next size) plus the tolerated share of the next size's band.
function cly_largestLimit(maxPos, nextPos) {
  return maxPos + (0.5 + cly_TOLERANCE) * (nextPos - maxPos);
}

function cly_letterFor(n, twoXL) {
  if (n === 4) return twoXL ? '2XL' : 'XXL';
  return cly_LETTER_BY_POS[n] || null;
}

// The size the customer needs at position p, named in the product's own system: { label, alt } or null when that
// cannot be done reliably (unusual or mixed sizing, or beyond every known size). Nothing is invented: null means
// "tell the customer the product does not go up to their size" without naming one.
//   letters : "XL"            ordinal : "4" (numeric products are never described with letters)
//   euWomen : "44" (store chart only)   rank : continues the product's own numbers when their step is regular
function cly_neededSize(items, system, p) {
  var n = Math.round(p);
  if (system === 'letters' || system === 'ordinal') {
    var twoXL = items.some(function (i) { return /^\dXL/.test(String(i.value).toUpperCase().replace(/\s+/g, '')); });
    var letter = system === 'letters' ? cly_letterFor(n, twoXL) : null;
    if (system === 'ordinal') return n + 1 >= 0 ? { label: String(n + 1), alt: null } : null;   // numbers only; nothing below 0
    return letter ? { label: letter, alt: null } : null;
  }
  if (system === 'euWomen') {
    var best = null;
    Object.keys(cly_EU_WOMEN_POS).forEach(function (k) {
      var d = Math.abs(cly_EU_WOMEN_POS[k] - p);
      if (d <= 0.5 + 1e-9 && (best === null || d < best.d)) best = { label: k, d: d };
    });
    return best ? { label: best.label, alt: null } : null;
  }
  if (system === 'rank') {
    var nums = items.map(function (i) { return i.num; }).sort(function (a, b) { return a - b; });
    var step = nums[1] - nums[0];
    var uniform = step > 0;
    for (var i = 2; i < nums.length && uniform; i++) if (nums[i] - nums[i - 1] !== step) uniform = false;
    if (!uniform) return null;
    var num = n < 0 ? nums[0] + n * step : nums[nums.length - 1] + (n - (nums.length - 1)) * step;
    return num > 0 ? { label: String(num), alt: null } : null;
  }
  return null;
}

// h in cm, w in kg, fit in slim | regular | oversized.
function cly_recommend(h, w, fit, items) {
  if (!items || items.length < 2) return { status: 'noEquivalence' };
  var avail = items.filter(function (it) { return it.available; });
  if (!avail.length) return { status: 'soldOut' };
  var p = cly_position(h, w) + (cly_FIT_SHIFT[fit] || 0);      // the customer's size, whatever this product sells
  var positions = items.map(function (i) { return i.pos; });
  var maxPos = Math.max.apply(null, positions), minPos = Math.min.apply(null, positions);
  var system = cly_system(items);
  var limit = cly_largestLimit(maxPos, cly_nextPos(system, maxPos));
  if (p > limit) {
    return { status: 'tooLarge', position: p, limit: limit, needed: cly_neededSize(items, system, p) };
  }
  var lowLimit = cly_smallestLimit(minPos, cly_prevPos(system, minPos));
  if (p < lowLimit) {
    return { status: 'tooSmall', position: p, limit: lowLimit, needed: cly_neededSize(items, system, p) };
  }
  var ideal = cly_nearest(items, p, fit);
  var pick = cly_nearest(avail, p, fit);
  return {
    status: 'ok',
    pick: pick,
    position: p,
    soldNote: ideal.value !== pick.value ? ideal.value : null,
    edge: minPos - p > cly_EDGE_GAP ? 'smallest' : null
  };
}

if (typeof module !== 'undefined') {
  module.exports = { cly_recommend: cly_recommend, cly_mapSizes: cly_mapSizes, cly_position: cly_position, cly_system: cly_system, cly_neededSize: cly_neededSize, cly_prevPos: cly_prevPos };
}
